<template>
  <div class="input-wrapper password-wrapper" :class="{ 'focused': isFocused || hasValue }"
    @mouseenter="showClearIconMouseHold = true" @mouseleave="showClearIconMouseHold = false">
    <input id="password-input-elm" class="style-input" :type="fieldType" name="password"
      autocomplete="current-password" :value="modelValue"
      @input="handleInput" @focus="onFocus" @blur="onBlur" ref="inputRef" :style="inputStyle" />

    <label class="floating-label" :class="{ 'active': isFocused || hasValue }">
      {{ $t('Login.Password') }}
    </label>

    <span class="search_buttons_container" v-if="showIcons">

      <img v-if="showClearIcon" :src="iconClosePath" width="16" height="16" class="pointer" @click="clearField"
        @mouseenter="showClearHint = true" @mouseleave="showClearHint = false" @mousedown.prevent="clearField" />

      <div v-if="showClearHint" class="hint-container">
        <div class="hint-content-email">
          {{ $t('Login.ClearField') }}
        </div>
      </div>

      <img v-if="error && !isFocused" :src="iconInvalidPath" width="16" height="16" class="pointer" @mouseenter="showErrorHint = true"
        @mouseleave="showErrorHint = false" />

      <div v-if="showErrorHint" class="hint-container">
        <div class="hint-content-email">
          {{ $t('Login.CheckPassword') }}
        </div>
      </div>

      <img :src="visibilityIcon" width="16" height="16" class="pointer" @click="toggleVisibility"
        @mouseenter="showVisibilityHint = true" @mouseleave="showVisibilityHint = false" />

      <div v-if="!isVisible && showVisibilityHint" class="hint-container">
        <div class="hint-content-email">
          {{ $t('Login.ShowPassword') }}
        </div>
      </div>

      <div v-if="isVisible && showVisibilityHint" class="hint-container">
        <div class="hint-content-email">
          {{ $t('Login.HidePassword') }}
        </div>
      </div>
    </span>
  </div>
</template>

<script lang="ts">
import { ref, computed, onMounted, watch } from 'vue';

import '@/styles/inputs.scss';

export default {
  name: 'PasswordInput',
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    error: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue', 'clear-error', 'clear-field'],
  setup(props: any, context: any) {
    const iconInvalidPath = new URL('@/assets/image/icon-invalid.svg', import.meta.url).href;
    const iconVisible = new URL('@/assets/image/icon-visible.svg', import.meta.url).href;
    const iconUnvisible = new URL('@/assets/image/icon-unvisible.svg', import.meta.url).href;
    const iconClosePath = new URL('@/assets/image/icon-arrow.svg', import.meta.url).href;

    const inputRef = ref<HTMLInputElement>();
    const isFocused = ref(false);
    const isVisible = ref(false);
    const showErrorHint = ref(false);
    const showVisibilityHint = ref(false);
    const showClearHint = ref(false);
    const showClearIconMouseHold = ref(false);
    const hasValue = ref(false);

    const checkAutofill = () => {
      if (inputRef.value) {
        const isAutofilled =
          inputRef.value.matches(':-webkit-autofill') ||
          inputRef.value.matches(':autofill') ||
          inputRef.value.value.length > 0;

        hasValue.value = isAutofilled || (props.modelValue && props.modelValue.length > 0);
      }
    };

    const showIcons = computed(() => {
      return isFocused.value || (props.modelValue && props.modelValue.length > 0);
    });

    const showClearIcon = computed(() => {
      return (isFocused.value || showClearIconMouseHold.value) && props.modelValue.length > 0;
    });

    const inputStyle = computed(() => {
      let visibleIconsCount = 0;

      if (showClearIcon.value) visibleIconsCount++;
      if (props.error) visibleIconsCount++;
      if (isFocused.value || props.modelValue.length) visibleIconsCount++;

      const basePadding = 16;
      const iconWidth = 16;
      const iconGap = 4;
      const additionalPadding = visibleIconsCount * (iconWidth + iconGap);

      return {
        paddingRight: `${basePadding + additionalPadding}px`
      };
    });

    const fieldType = computed(() => {
      return isVisible.value ? 'text' : 'password';
    });

    const visibilityIcon = computed(() => {
      return isVisible.value ? iconUnvisible : iconVisible;
    });

    const handleInput = (event: Event) => {
      const target = event.target as HTMLInputElement;
      context.emit('update:modelValue', target.value);
      context.emit('clear-error');
    };

    const onFocus = () => {
      isFocused.value = true;
    };

    const onBlur = () => {
      setTimeout(() => {
        isFocused.value = false;
        showClearHint.value = false;
      }, 100);
    };

    const toggleVisibility = () => {
      isVisible.value = !isVisible.value;
    };

    const clearField = (event: Event) => {
      event.preventDefault();

      showClearHint.value = false;

      context.emit('update:modelValue', '');
      context.emit('clear-field');
      context.emit('clear-error');

      setTimeout(() => {
        if (inputRef.value) {
          isFocused.value = true;
          inputRef.value.focus();
        }
      }, 0);
    };

    watch(() => props.modelValue, (newValue) => {
      hasValue.value = newValue && newValue.length > 0;

      if (!newValue && props.error) {
        context.emit('clear-error');
      }
    });

    onMounted(() => {
      setTimeout(() => {
        checkAutofill();
      }, 100);
    });

    return {
      iconInvalidPath,
      iconVisible,
      iconUnvisible,
      iconClosePath,
      inputRef,
      isFocused,
      isVisible,
      showErrorHint,
      showClearHint,
      showClearIconMouseHold,
      showVisibilityHint,
      hasValue,
      showIcons,
      showClearIcon,
      inputStyle,
      fieldType,
      visibilityIcon,
      handleInput,
      onFocus,
      onBlur,
      toggleVisibility,
      clearField
    };
  }
};
</script>

<style scoped>
.input-wrapper {
  position: relative;
  display: inline-block;

  &:hover .style-input {
    border: 1px solid #3F884F;
  }
}

.password-wrapper.focused .style-input {
  padding-top: 8px;
}

.password-wrapper:not(.focused) .floating-label {
  top: 22%;
  font-size: 14px;
  color: #999;
}

.search_buttons_container {
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  display: flex;
  gap: 8px;
  align-items: center;
}

.hint-container {
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: 8px;
  z-index: 1000;
}

.hint-content-email {
  background: #5D5D5D;
  opacity: 0.9;
  color: white;
  width: max-content;
  height: 20px;
  padding: 2px 8px;
  border-radius: 8px;
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  backdrop-filter: blur(4px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.pointer {
  cursor: pointer;
}

.floating-label {
  position: absolute;
  left: 12px;
  top: 24%;
  color: #9C9C9C;
  font-size: 14px;
  pointer-events: none;
  transition: all 0.3s ease;
  /* background: white; */
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  transform: translateZ(0);
  backface-visibility: hidden;
}

.floating-label.active {
  top: 1px;
  font-size: 10px;
  background: none;
}

#password-input-elm::-ms-reveal,
#password-input-elm::-ms-clear {
  display: none;
}

#password-input-elm::-webkit-credentials-auto-fill-button,
#password-input-elm::-webkit-caps-lock-indicator {
  display: none;
  visibility: hidden;
  pointer-events: none;
}

@media (max-width: 768px) {
  .style-input {
    width: 100% !important;
  }

  .input-wrapper {
    width: 100%;
  }
}
</style>
